import {
    AfterViewInit,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from "@angular/forms";
import { NgSelectComponent, NgHeaderTemplateDirective, NgMultiLabelTemplateDirective } from "@ng-select/ng-select";
import {
    BehaviorSubject,
    merge,
    Observable,
    of,
    Subject
} from "rxjs";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    tap
} from "rxjs/operators";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { RequiredDirective } from "../../directives/required/required.directive";
import { InfoComponent } from "../info/info.component";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { AsyncPipe } from "@angular/common";
@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchComponent)
        }
    ],
    selector: "rask-search",
    styleUrls: ["./search.component.scss"],
    templateUrl: "./search.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        NgSelectComponent,
        FormsModule,
        NgHeaderTemplateDirective,
        NgMultiLabelTemplateDirective,
        ValidationMessageComponent,
        AsyncPipe
    ]
})
export class SearchComponent extends ControlValueAccessorBaseComponent implements AfterViewInit, OnInit, ControlValueAccessor {

    @Input() selected = null;
    @Input() items: Observable<any[]>;
    @Input() hideSelected = false;
    @Input() clearAllTooltipText: string;
    @Input() bindLabel?: string;
    @Input() bindValue?: string;
    @Input() groupBy?: string;
    @Input() searchFn = null;
    @Input() typeToSearchText?: string;
    @Input() loadingText?: string;
    @Input() notFoundText?: string;
    @Input() minTermLength?: number;
    @Input() typeaheadFn: (input: string) => Observable<any[]>;
    @Input() valueLookup: (value: any) => Observable<any[]>;
    @Input() readonly = false;
    @Input() compareWith = null;
    @Input() debounceTime = 500;
    @Input() headerOptions: IRaskSearchHeaderOption<any>[];
    @Output() selectedChanged = new EventEmitter<any>();
    @ViewChild("instance") instance: NgSelectComponent;

    loading = false;
    typeaheadInput$ = new Subject<string>();
    defaults$ = new BehaviorSubject<any[]>([]);
    constructor() {
        super();
    }

    onSelectionChange($event: any) {
        this.selectedChanged.emit($event);
        if ($event === undefined) {
            this.value = null;
            this.defaults$.next([]);
        } else if (this.bindValue) {
            this.value = $event[this.bindValue];
        } else {
            this.value = $event;
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.typeaheadFn) {
            this.loadData();
        }
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
    }

    getLabel(item: any): string {
        if (this.bindLabel) {
            return item[this.bindLabel];
        }

        return item;
    }

    loadData() {
        if (this.valueLookup && !this.value && this.selected) {
            this.valueLookup(this.selected).subscribe((data) => {
                this.defaults$.next(data);
            });
        }

        this.items = merge(
            this.defaults$, // default items
            this.typeaheadInput$.pipe(
                debounceTime(this.debounceTime),
                distinctUntilChanged(),
                tap(() => this.loading = true),
                switchMap(term => this.getData(term).pipe(
                    catchError(() => of([])), // empty list on error
                    tap(() => this.loading = false)
                ))
            )
        );
    }

    getData(term: string = null): Observable<any[]> {
        if (!term) {
            return of([]);
        }

        return this.typeaheadFn(term);
    }

    protected headerOptionClick(headerOption: IRaskSearchHeaderOption<any>) {
        this.onSelectionChange(headerOption.data);
        this.instance.close();
        this.instance.blur();
    }
}

export interface IRaskSearchHeaderOption<T> {
    data: T;
}
